// Admin-Panel › Discord-Brücke: Status, Bot-Einladung, Import, gespiegelte Server (Fortschritt), Kanal-Modi.
import { BRIDGE_CHANNEL_MODES, type BridgeChannelDto, type BridgeChannelMode, type BridgeGuildDto, type BridgeStatusDto } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { adminApi, lookupApi, type AvailableGuild } from '../api';
import { copyText, formatDateTime, formatNumber } from '../format';
import { toast } from '../toast';
import { Badge, Button, Dialog, Empty, ErrorBox, Field, Loading, ProgressBar } from '../ui';
import {
  bridgeInviteUrl,
  CHANNEL_MODE_HELP,
  CHANNEL_MODE_LABELS,
  GUILD_STATUS_LABELS,
  GUILD_STATUS_TONES,
  HISTORY_PRESETS,
  IMPORT_PHASE_LABELS,
  isBridgeBusy,
  progressPercent,
} from './bridge-utils';
import { AdminError, AdminSection } from './parts';
import { ADMIN_AVAILABLE_GUILDS_KEY, ADMIN_BRIDGE_KEY } from './realtime';
import { sameOriginUrl } from '../safe-url';

const STATE_LABELS: Record<BridgeStatusDto['state'], { label: string; tone: 'neutral' | 'accent' | 'ok' | 'danger' }> = {
  idle: { label: 'Leerlauf', tone: 'neutral' },
  connecting: { label: 'Verbindet', tone: 'accent' },
  ready: { label: 'Verbunden', tone: 'ok' },
  error: { label: 'Fehler', tone: 'danger' },
};

function useBridgeMutation<TArg>(fn: (arg: TArg) => Promise<unknown>, success?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_BRIDGE_KEY });
      if (success) toast({ kind: 'success', title: success });
    },
  });
}

function InviteBox(props: { status: BridgeStatusDto }) {
  const [copied, setCopied] = useState(false);
  const url = bridgeInviteUrl(props.status.botUser?.id);
  if (!url || props.status.state === 'idle') {
    return (
      <div className="lfx-card lfx-muted" data-testid="admin-bridge-no-bot">
        Kein Brücken-Bot aktiv: In der Server-Konfiguration fehlt das Bot-Token (<code className="lfx-mono">DISCORD_BOT_TOKEN</code>) bzw. das
        Brücken-Geheimnis (<code className="lfx-mono">BRIDGE_SECRET</code>). Eintragen und den Brücken-Dienst neu starten.
      </div>
    );
  }
  return (
    <div className="lfx-stack" data-testid="admin-bridge-invite">
      <div className="lfx-field__label">Bot einladen</div>
      <p className="lfx-muted">Mit diesem Link lädst du den Bot mit den nötigen Rechten (lesen, schreiben, Webhooks, Verlauf) in einen Discord-Server ein.</p>
      <div className="lfx-secret__row">
        <code className="lfx-secret__value" data-testid="admin-bridge-invite-url">
          {url}
        </code>
        <Button testId="admin-bridge-invite-copy" onClick={() => void copyText(url).then(setCopied)}>
          {copied ? 'Kopiert' : 'Kopieren'}
        </Button>
        <a className="lfx-btn lfx-btn--ghost" href={url} target="_blank" rel="noopener noreferrer" data-testid="admin-bridge-invite-open">
          Öffnen
        </a>
      </div>
    </div>
  );
}

function ImportDialog(props: { guild: AvailableGuild; onClose: () => void }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<'0' | '1000' | '10000' | 'custom'>('1000');
  const [custom, setCustom] = useState('5000');
  const [mode, setMode] = useState<BridgeChannelMode>('READONLY');
  const customValue = /^\d+$/.test(custom.trim()) ? Number(custom.trim()) : null;
  const historyLimit = preset === 'custom' ? customValue : Number(preset);
  const run = useMutation({
    mutationFn: () => adminApi.importGuild({ discordGuildId: props.guild.discordGuildId, historyLimit: historyLimit ?? 0, defaultMode: mode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_BRIDGE_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_AVAILABLE_GUILDS_KEY });
      toast({ kind: 'success', title: 'Import gestartet', body: props.guild.name });
      props.onClose();
    },
  });
  return (
    <Dialog
      title={`„${props.guild.name}" importieren`}
      testId="admin-bridge-import-dialog"
      onClose={props.onClose}
      footer={
        <>
          <Button testId="admin-bridge-import-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" testId="admin-bridge-import-submit" disabled={historyLimit === null || run.isPending} onClick={() => run.mutate()}>
            Import starten
          </Button>
        </>
      }
    >
      <fieldset className="lfx-admin-fieldset">
        <legend className="lfx-field__label">Verlauf je Kanal</legend>
        {HISTORY_PRESETS.map((p) => (
          <label key={p.value} className="lfx-check">
            <input
              type="radio"
              name="admin-bridge-history"
              checked={preset === String(p.value)}
              data-testid={`admin-bridge-import-history-${p.value}`}
              onChange={() => setPreset(String(p.value) as '0' | '1000' | '10000')}
            />
            {p.value === 0 ? 'Alles (kann lange dauern)' : `Letzte ${p.label} Nachrichten`}
          </label>
        ))}
        <label className="lfx-check">
          <input type="radio" name="admin-bridge-history" checked={preset === 'custom'} data-testid="admin-bridge-import-history-custom" onChange={() => setPreset('custom')} />
          Eigene Anzahl
        </label>
        {preset === 'custom' && (
          <Field label="Nachrichten je Kanal (0 = alles)" error={customValue === null ? 'Bitte eine ganze Zahl ≥ 0 eingeben.' : null}>
            {(id) => (
              <input id={id} className="lfx-input" inputMode="numeric" value={custom} data-testid="admin-bridge-import-history-input" onChange={(e) => setCustom(e.target.value)} />
            )}
          </Field>
        )}
      </fieldset>
      <Field label="Standardmodus für Kanäle" hint={CHANNEL_MODE_HELP[mode]}>
        {(id) => (
          <select id={id} className="lfx-select" value={mode} data-testid="admin-bridge-import-mode" onChange={(e) => setMode(e.target.value as BridgeChannelMode)}>
            {BRIDGE_CHANNEL_MODES.map((m) => (
              <option key={m} value={m}>
                {CHANNEL_MODE_LABELS[m]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <ErrorBox error={run.error} testId="admin-bridge-import-error" />
    </Dialog>
  );
}

function RemoveDialog(props: { guild: BridgeGuildDto; onClose: () => void }) {
  const [deleteServer, setDeleteServer] = useState(false);
  const remove = useBridgeMutation(() => adminApi.removeGuild(props.guild.id, deleteServer), 'Spiegelung entfernt');
  return (
    <Dialog
      title={`Spiegelung „${props.guild.name}" entfernen`}
      testId="admin-bridge-remove-dialog"
      onClose={props.onClose}
      footer={
        <>
          <Button testId="admin-bridge-remove-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button variant="danger" testId="admin-bridge-remove-submit" disabled={remove.isPending} onClick={() => remove.mutate(undefined, { onSuccess: props.onClose })}>
            Entfernen
          </Button>
        </>
      }
    >
      <p>Die Brücke hört auf, diesen Discord-Server zu spiegeln.</p>
      <label className="lfx-check">
        <input type="checkbox" checked={deleteServer} data-testid="admin-bridge-remove-delete-server" onChange={(e) => setDeleteServer(e.target.checked)} />
        Plattform-Server ebenfalls löschen
      </label>
      <p className="lfx-muted">
        {deleteServer
          ? 'Der Server mit allen gespiegelten Nachrichten wird endgültig gelöscht.'
          : 'Der Server bleibt als normaler (nicht mehr gespiegelter) Server erhalten.'}
      </p>
      <ErrorBox error={remove.error} testId="admin-bridge-remove-error" />
    </Dialog>
  );
}

function ChannelRow(props: { channel: BridgeChannelDto; name: string | null }) {
  const c = props.channel;
  const setMode = useBridgeMutation((mode: BridgeChannelMode) => adminApi.setChannelMode(c.id, mode), 'Kanalmodus geändert');
  return (
    <tr data-testid={`admin-bridge-channel-${c.id}`}>
      <td>
        {props.name ? `#${props.name}` : <span className="lfx-mono">{c.channelId}</span>}
        <div className="lfx-muted lfx-mono">Discord {c.discordChannelId}</div>
      </td>
      <td>
        <select
          className="lfx-select"
          aria-label={`Modus für ${props.name ?? c.channelId}`}
          value={c.mode}
          disabled={setMode.isPending}
          data-testid={`admin-bridge-channel-mode-${c.id}`}
          onChange={(e) => setMode.mutate(e.target.value as BridgeChannelMode)}
        >
          {BRIDGE_CHANNEL_MODES.map((m) => (
            <option key={m} value={m}>
              {CHANNEL_MODE_LABELS[m]}
            </option>
          ))}
        </select>
        <ErrorBox error={setMode.error} testId={`admin-bridge-channel-error-${c.id}`} />
      </td>
      <td data-testid={`admin-bridge-channel-backfill-${c.id}`}>
        {c.backfillDone ? <Badge tone="ok">fertig</Badge> : <Badge tone="accent">läuft</Badge>}
        {c.backfillCursor && <div className="lfx-muted lfx-mono">bis {c.backfillCursor}</div>}
      </td>
      <td>{c.lastError ? <span className="lfx-field__error">{c.lastError}</span> : '–'}</td>
    </tr>
  );
}

function ChannelTable(props: { guild: BridgeGuildDto }) {
  // Namen der Plattform-Kanäle (BridgeChannelDto enthält keinen Namen) – optional, Fehler werden ignoriert.
  const names = useQuery({
    queryKey: ['admin', 'bridge', 'channel-names', props.guild.serverId],
    queryFn: () => lookupApi.channels(props.guild.serverId),
    retry: false,
  });
  const nameOf = (channelId: string) => names.data?.find((ch) => ch.id === channelId)?.name ?? null;
  if (props.guild.channels.length === 0) return <Empty>Noch keine Kanäle gespiegelt.</Empty>;
  return (
    <div className="lfx-table-wrap">
      <table className="lfx-table" data-testid={`admin-bridge-channels-${props.guild.id}`}>
        <thead>
          <tr>
            <th scope="col">Kanal</th>
            <th scope="col">Modus</th>
            <th scope="col">Verlauf</th>
            <th scope="col">Letzter Fehler</th>
          </tr>
        </thead>
        <tbody>
          {props.guild.channels.map((c) => (
            <ChannelRow key={c.id} channel={c} name={nameOf(c.channelId)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GuildCard(props: { guild: BridgeGuildDto }) {
  const g = props.guild;
  const [showChannels, setShowChannels] = useState(false);
  const [removing, setRemoving] = useState(false);
  const closeRemove = useCallback(() => setRemoving(false), []);
  const toggle = useBridgeMutation(
    (status: 'PAUSED' | 'ACTIVE') => adminApi.updateGuild(g.id, { status }),
    g.status === 'PAUSED' ? 'Spiegelung fortgesetzt' : 'Spiegelung pausiert',
  );
  const resync = useBridgeMutation(() => adminApi.resyncGuild(g.id), 'Neusynchronisierung angestoßen');
  const pct = progressPercent(g.importProgress);
  const p = g.importProgress;
  return (
    <li className="lfx-card lfx-stack" data-testid={`admin-bridge-guild-${g.id}`}>
      <div className="lfx-row lfx-row--between">
        <div className="lfx-row">
          <strong>{g.name}</strong>
          <Badge tone={GUILD_STATUS_TONES[g.status]} testId={`admin-bridge-guild-status-${g.id}`}>
            {GUILD_STATUS_LABELS[g.status]}
          </Badge>
        </div>
        <div className="lfx-row">
          {g.status === 'PAUSED' ? (
            <Button testId={`admin-bridge-guild-resume-${g.id}`} disabled={toggle.isPending} onClick={() => toggle.mutate('ACTIVE')}>
              Fortsetzen
            </Button>
          ) : (
            <Button testId={`admin-bridge-guild-pause-${g.id}`} disabled={toggle.isPending || g.status === 'PENDING'} onClick={() => toggle.mutate('PAUSED')}>
              Pausieren
            </Button>
          )}
          <Button testId={`admin-bridge-guild-resync-${g.id}`} disabled={resync.isPending} onClick={() => resync.mutate(undefined)}>
            Neu synchronisieren
          </Button>
          <Button variant="danger" testId={`admin-bridge-guild-remove-${g.id}`} onClick={() => setRemoving(true)}>
            Entfernen
          </Button>
        </div>
      </div>
      {p && (
        <div className="lfx-stack lfx-admin-progress" data-testid={`admin-bridge-guild-progress-${g.id}`}>
          <div className="lfx-row lfx-row--between lfx-muted">
            <span>Phase: {IMPORT_PHASE_LABELS[p.phase] ?? p.phase}</span>
            <span data-testid={`admin-bridge-guild-progress-text-${g.id}`}>
              {formatNumber(p.done)} / {p.total > 0 ? formatNumber(p.total) : '?'}
              {pct !== null && ` (${pct} %)`}
            </span>
          </div>
          <ProgressBar value={p.done} max={p.total > 0 ? p.total : Math.max(p.done, 1)} label={`Importfortschritt ${g.name}`} testId={`admin-bridge-guild-progressbar-${g.id}`} />
        </div>
      )}
      <div className="lfx-muted">
        Discord-ID <span className="lfx-mono">{g.discordGuildId}</span> · Verlauf: {g.historyLimit === 0 ? 'alles' : `${formatNumber(g.historyLimit)} je Kanal`} · Letzte
        Synchronisierung: {formatDateTime(g.lastSyncAt)}
      </div>
      {g.lastError && (
        <div className="lfx-error" data-testid={`admin-bridge-guild-error-${g.id}`}>
          {g.lastError}
        </div>
      )}
      <ErrorBox error={toggle.error ?? resync.error} testId={`admin-bridge-guild-action-error-${g.id}`} />
      <div>
        <button
          type="button"
          className="lfx-admin-textbtn"
          aria-expanded={showChannels}
          data-testid={`admin-bridge-guild-channels-toggle-${g.id}`}
          onClick={() => setShowChannels((v) => !v)}
        >
          {showChannels ? 'Kanäle ausblenden' : `Kanäle anzeigen (${g.channels.length})`}
        </button>
      </div>
      {showChannels && <ChannelTable guild={g} />}
      {removing && <RemoveDialog guild={g} onClose={closeRemove} />}
    </li>
  );
}

function AvailableGuilds() {
  const [importing, setImporting] = useState<AvailableGuild | null>(null);
  const closeImport = useCallback(() => setImporting(null), []);
  const query = useQuery({ queryKey: ADMIN_AVAILABLE_GUILDS_KEY, queryFn: adminApi.availableGuilds });
  return (
    <AdminSection
      title="Discord-Server des Bots"
      testId="admin-bridge-available"
      actions={
        <Button testId="admin-bridge-available-refresh" onClick={() => void query.refetch()}>
          Aktualisieren
        </Button>
      }
    >
      {query.isLoading && <Loading testId="admin-bridge-available-loading" />}
      <AdminError error={query.error} testId="admin-bridge-available-error" />
      {query.data && query.data.length === 0 && <Empty testId="admin-bridge-available-empty">Der Bot ist noch in keinem Discord-Server – nutze den Einladungslink.</Empty>}
      {query.data && query.data.length > 0 && (
        <ul className="lfx-admin-list">
          {query.data.map((g) => (
            <li key={g.discordGuildId} className="lfx-row lfx-row--between lfx-admin-guildrow" data-testid={`admin-bridge-available-${g.discordGuildId}`}>
              <span className="lfx-row">
                {sameOriginUrl(g.iconUrl) ? <img src={sameOriginUrl(g.iconUrl)!} alt="" className="lfx-admin-guildicon" /> : <span className="lfx-admin-guildicon" aria-hidden="true" />}
                <strong>{g.name}</strong>
                <span className="lfx-muted">{formatNumber(g.memberCount)} Mitglieder</span>
                {g.mirrored && (
                  <Badge tone="mirror" testId={`admin-bridge-available-mirrored-${g.discordGuildId}`}>
                    gespiegelt
                  </Badge>
                )}
              </span>
              {!g.mirrored && (
                <Button variant="primary" testId={`admin-bridge-available-import-${g.discordGuildId}`} onClick={() => setImporting(g)}>
                  Importieren
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {importing && <ImportDialog guild={importing} onClose={closeImport} />}
    </AdminSection>
  );
}

export function BridgePage() {
  const query = useQuery({
    queryKey: ADMIN_BRIDGE_KEY,
    queryFn: adminApi.bridge,
    refetchInterval: (q) => (isBridgeBusy(q.state.data?.guilds) ? 3000 : false),
  });
  const s = query.data;
  const state = s ? STATE_LABELS[s.state] : null;
  const hasBot = !!s?.botUser && s.state !== 'idle';
  return (
    <div className="lfx-stack" data-testid="admin-bridge">
      <AdminSection title="Discord-Brücke" testId="admin-bridge-status">
        {query.isLoading && <Loading testId="admin-bridge-loading" />}
        <AdminError error={query.error} testId="admin-bridge-error" />
        {s && (
          <div className="lfx-stack">
            <div className="lfx-row">
              {state && (
                <Badge tone={state.tone} testId="admin-bridge-state">
                  {state.label}
                </Badge>
              )}
              <span className="lfx-muted" data-testid="admin-bridge-bot">
                {s.botUser ? `Bot: ${s.botUser.username}` : 'Kein Bot'}
              </span>
              <span className="lfx-muted">Stand {formatDateTime(s.updatedAt)}</span>
            </div>
            {s.lastError && (
              <div className="lfx-error" data-testid="admin-bridge-last-error">
                {s.lastError}
              </div>
            )}
            <InviteBox status={s} />
          </div>
        )}
      </AdminSection>
      {hasBot && <AvailableGuilds />}
      {s && (
        <AdminSection title="Gespiegelte Server" testId="admin-bridge-guilds">
          {s.guilds.length === 0 ? (
            <Empty testId="admin-bridge-guilds-empty">Noch keine Spiegelungen.</Empty>
          ) : (
            <ul className="lfx-admin-list lfx-stack">
              {s.guilds.map((g) => (
                <GuildCard key={g.id} guild={g} />
              ))}
            </ul>
          )}
          <details className="lfx-admin-help" data-testid="admin-bridge-mode-help">
            <summary>Was bedeuten die Kanal-Modi?</summary>
            <dl>
              {BRIDGE_CHANNEL_MODES.map((m) => (
                <div key={m}>
                  <dt>{CHANNEL_MODE_LABELS[m]}</dt>
                  <dd>{CHANNEL_MODE_HELP[m]}</dd>
                </div>
              ))}
            </dl>
          </details>
        </AdminSection>
      )}
    </div>
  );
}
